import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { forgetContact, readQuizResult, readSignup, rememberContact, rememberSignup, toE164 } from '@theme/klaviyo-client';
import { changeMetaThemeColor } from '@theme/utilities';

/**
 * Signup popup: prize cards → one question → email → texts → reward.
 *
 * Klaviyo wiring (all best-effort; nothing here can strand the visitor):
 * - Email and phone are subscribed through Klaviyo's client Subscriptions API
 *   with the section's public key and list IDs. Each request grants only the
 *   channel it collects. The SMS list's own opt-in setting decides whether
 *   Klaviyo texts a "reply Y" confirmation before the number is subscribed;
 *   the section's "double opt-in" setting keeps the reward copy in step.
 * - After a successful email the onsite script is told who the visitor is
 *   (`klaviyo.identify`), and a "Signup Popup Completed" event is recorded
 *   through the client Events API when the flow finishes, so Klaviyo flows
 *   can react to the signup.
 * - The component listens for Klaviyo's `klaviyoForms` window events, so a
 *   signup through a Klaviyo-hosted form (email or SMS) also marks the visitor
 *   as subscribed, and it asks `klaviyo.isIdentified` before a timed open so
 *   recognized subscribers are not prompted again.
 * - Every signup dispatches `signup-popup:subscribed` on `document` with
 *   `{ channel, source }` (no personal data) for analytics listeners, and
 *   notes the signup under the shared `tiiga-signup:subscribed` key. A
 *   signup the product quiz recorded there is adopted before a timed open,
 *   so the popup stops asking a visitor the quiz already subscribed.
 * - A visitor who finished the product quiz (noted under the shared
 *   `tiiga-quiz:completed` key) has already said what brings them here, so
 *   the popup skips its one question after the cards and goes straight to
 *   email. The quiz's reason stands in for the answer: mapped onto one of
 *   the popup's goals when it fits, and sent as
 *   `signup_popup_quiz_answer_source` / `signup_popup_quiz_reason` either way.
 * - The email and phone Klaviyo accepted are kept under the shared
 *   `tiiga-signup:contact` key so the cart's checkout button can prefill
 *   the checkout with them (assets/checkout-prefill.js). A Klaviyo-hosted
 *   form's email and phone are kept the same way.
 *
 * Returning subscribers who reopen the popup get the "already subscribed"
 * screen. Nothing here verifies a phone or creates a discount code: the
 * savings are the storefront's own first-subscription-order bundle offers,
 * read from the offer product's buy box.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} steps
 * @property {HTMLElement} matchStatus
 * @property {HTMLInputElement} emailInput
 * @property {HTMLElement} emailError
 * @property {HTMLButtonElement} emailSubmit
 * @property {HTMLSelectElement} [countryCode]
 * @property {HTMLInputElement} [phoneInput]
 * @property {HTMLElement} [phoneError]
 * @property {HTMLButtonElement} [phoneSubmit]
 *
 * @typedef {{ status?: string, at?: number, closed?: number, sms?: string, source?: string }} SignupRecord
 * @typedef {{ count: number, percent: number }} OfferTier
 * @typedef {'game' | 'quiz' | 'email' | 'phone' | 'success' | 'subscribed'} Step
 */
class SignupPopupComponent extends DialogComponent {
  requiredRefs = ['dialog', 'steps'];

  /** @type {Step[]} */
  static steps = ['game', 'quiz', 'email', 'phone', 'success', 'subscribed'];
  static matchesNeeded = 3;
  static klaviyoRevision = '2026-07-15';
  static subscriptionsEndpoint = 'https://a.klaviyo.com/client/subscriptions/';
  static eventsEndpoint = 'https://a.klaviyo.com/client/events/';
  static completedMetric = 'Signup Popup Completed';
  /** On article pages, how long after the reader finishes the article the timed popup waits, so it does not land the moment they reach the buy box. */
  static afterReadingDelay = 20000;
  static customSource = 'Tiiga signup popup';

  /** @type {Step} */
  #step = 'game';
  #email = '';
  #phone = '';
  #answer = '';
  #answerLabel = '';
  /** @type {'' | 'popup' | 'product-quiz'} */
  #answerSource = '';
  /** The product quiz's reason key when it supplied the answer. */
  #quizReason = '';
  /** @type {'' | 'pending' | 'requested'} */
  #sms = '';
  #flipped = 0;
  #busy = false;
  #finished = false;
  /** Klaviyo recognized this visitor during the timed check. */
  #known = false;
  /** ?signup_reset=1: treat this page view as a brand-new visitor. */
  #ignoreIdentity = false;
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #advanceTimer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #celebrateTimer;
  /** @type {Promise<void> | undefined} */
  #offerReady;
  /** @type {Record<string, string>} */
  #strings = {};

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#strings = JSON.parse(this.querySelector('[data-strings]')?.textContent || '{}');
    this.addEventListener(DialogCloseEvent.eventName, this.#handleClose, { signal });
    document.addEventListener('click', this.#handleTrigger, { signal });
    document.addEventListener('shopify:section:select', this.#handleSectionSelect, { signal });
    document.addEventListener('shopify:section:deselect', this.#handleSectionDeselect, { signal });
    window.addEventListener('klaviyoForms', this.#handleKlaviyoForm, { signal });

    const notice = this.querySelector('[data-preview-notice]');
    if (notice instanceof HTMLElement) notice.hidden = !this.#preview;
    if (this.dataset.designMode === 'true') return;

    const query = new URLSearchParams(location.search);
    if (query.get('signup_reset') === '1') {
      this.#clear();
      forgetContact();
      this.#ignoreIdentity = true;
    }
    if (query.get('signup_popup') === '1' || this.#preview) {
      const requested = query.get('signup_step');
      if (this.#preview && isStep(requested)) this.#step = requested;
      this.#schedule(0);
      return;
    }

    const delay = Number(this.dataset.delay || 6) * 1000;
    const readingEnd = document.querySelector('[data-article-end]');
    if (readingEnd) {
      this.#waitForReader(readingEnd, delay, signal);
    } else {
      this.#schedule(delay, true);
    }
  }

  /**
   * On a page built to be read (an article or listicle), a timed popup lands
   * mid-sentence. Hold it until the reader has reached the end of the copy and
   * had `afterReadingDelay` with the buy box, or until a desktop visitor heads
   * for the browser chrome to leave. The usual rules in `#plan` still decide whether
   * it opens at all.
   *
   * @param {Element} readingEnd - Marker placed where the reading ends.
   * @param {number} delay - The normal timed delay; exit intent is ignored until it has passed.
   * @param {AbortSignal} signal
   */
  #waitForReader(readingEnd, delay, signal) {
    const connectedAt = Date.now();
    let triggered = false;

    /** @param {number} wait */
    const trigger = (wait) => {
      if (triggered) return;
      triggered = true;
      observer.disconnect();
      this.#schedule(wait, true);
    };

    // Below the viewport the marker is not intersecting and its top is positive;
    // anything else means the reader is at it or has already scrolled past.
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting || entry.boundingClientRect.top < 0) trigger(SignupPopupComponent.afterReadingDelay);
      }
    });
    observer.observe(readingEnd);
    signal.addEventListener('abort', () => observer.disconnect());

    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    document.documentElement.addEventListener(
      'mouseleave',
      (event) => {
        if (event.clientY <= 0 && Date.now() - connectedAt >= delay) trigger(0);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    clearTimeout(this.#advanceTimer);
    clearTimeout(this.#celebrateTimer);
    this.#controller?.abort();
    this.#offerReady = undefined;
    super.disconnectedCallback();
  }

  /**
   * Opens the popup. Visitors who already signed up (here or through a
   * Klaviyo form) see the "already subscribed" screen instead of the flow.
   */
  async open() {
    clearTimeout(this.#timer);
    if (!this.isConnected || this.refs.dialog.open) return;

    if (!this.#preview && !this.#finished && (this.#known || this.#adoptExternalSignup())) {
      this.#step = 'subscribed';
    } else if (this.#step === 'game' && this.#flipped >= SignupPopupComponent.matchesNeeded) {
      this.#step = this.#stepAfterGame();
    }

    if (this.#step !== 'subscribed') {
      this.#offerReady ??= this.#syncOffer();
      await this.#offerReady;
      if (!this.isConnected || this.refs.dialog.open) return;
      if (!this.#preview && !this.#finished && (this.#known || this.#adoptExternalSignup())) {
        this.#step = 'subscribed';
      }
    }

    this.#showStep(this.#step);
    this.showDialog();
    this.#tintBrowserChrome();
  }

  dismiss() {
    this.closeDialog();
  }

  /** @param {Event} event */
  flipCard(event) {
    const card = event.target instanceof Element ? event.target.closest('.signup-flow__card') : null;
    if (!(card instanceof HTMLElement) || card.hasAttribute('data-flipped')) return;
    if (this.#flipped >= SignupPopupComponent.matchesNeeded) return;

    card.setAttribute('data-flipped', '');
    card.setAttribute('aria-pressed', 'true');
    card.setAttribute('aria-label', this.#t('card_revealed', { number: card.dataset.number ?? '', prize: this.#offerLabel }));
    this.#flipped += 1;
    this.refs.matchStatus.textContent = this.#t('matches', { count: this.#flipped });

    if (this.#flipped === SignupPopupComponent.matchesNeeded) {
      // Let the last flip land, celebrate the match, then move on.
      const cards = card.parentElement;
      this.#celebrateTimer = setTimeout(() => cards?.setAttribute('data-matched', ''), 450);
      this.#advanceTimer = setTimeout(() => this.#showStep(this.#stepAfterGame()), 1500);
    }
  }

  skipGame() {
    clearTimeout(this.#advanceTimer);
    clearTimeout(this.#celebrateTimer);
    this.#showStep(this.#stepAfterGame());
  }

  /**
   * The step after the prize cards: the question, unless the product quiz
   * already answered it for this visitor, in which case email comes next.
   *
   * @returns {Step}
   */
  #stepAfterGame() {
    if (this.#answerSource === 'product-quiz') return 'email';
    if (this.#preview) return 'quiz';
    return this.#adoptQuizAnswer() ? 'email' : 'quiz';
  }

  /**
   * Takes the product quiz's outcome as the answer to "what brings you to
   * Tiiga" when this browser finished the quiz recently. The quiz's reason
   * becomes one of the popup's goals when it maps cleanly; otherwise the
   * question is still skipped and only the raw reason is recorded.
   *
   * @returns {boolean} Whether a quiz answer was adopted.
   */
  #adoptQuizAnswer() {
    const result = readQuizResult();
    if (!result?.at || Date.now() - result.at > QUIZ_ANSWER_MAX_AGE) return false;

    const reason = typeof result.reason === 'string' ? result.reason : '';
    const goal = QUIZ_REASON_GOALS[reason] ?? '';
    const option = goal ? this.querySelector(`.signup-flow__option[data-value="${goal}"]`) : null;

    this.#answerSource = 'product-quiz';
    this.#quizReason = reason;
    this.#answer = goal;
    this.#answerLabel = option?.textContent?.trim() ?? '';
    for (const button of this.querySelectorAll('.signup-flow__option')) {
      button.setAttribute('aria-pressed', String(button === option));
    }
    return true;
  }

  /** @param {Event} event */
  selectOption(event) {
    const option = event.target instanceof Element ? event.target.closest('[data-value]') : null;
    if (!(option instanceof HTMLElement)) return;

    this.#answer = option.dataset.value ?? '';
    this.#answerLabel = option.textContent?.trim() ?? '';
    this.#answerSource = 'popup';
    this.#quizReason = '';
    for (const button of this.querySelectorAll('.signup-flow__option')) {
      button.setAttribute('aria-pressed', String(button === option));
    }
    this.#showStep('email');
  }

  /** @param {SubmitEvent} event */
  async submitEmail(event) {
    event.preventDefault();
    if (this.#busy) return;

    const input = this.refs.emailInput;
    const email = input.value.trim();
    if (!input.checkValidity() || !EMAIL_PATTERN.test(email)) {
      this.#error('email', this.#t('email_invalid'));
      return;
    }
    this.#error('email', '');

    if (email !== this.#email) {
      this.#setBusy('email', true);
      const ok = await this.#subscribe('email', { email });
      this.#setBusy('email', false);
      if (!this.isConnected) return;
      if (!ok) {
        this.#error('email', this.#t(this.dataset.klaviyoKey ? 'error_generic' : 'unavailable'));
        return;
      }
      this.#email = email;
      this.#identify();
      if (this.#recordsHistory) {
        this.#save('subscribed');
        rememberSignup('popup', 'email');
        rememberContact({ email });
      }
      this.#announce('email');
    }

    if (this.dataset.klaviyoSmsList && this.refs.phoneInput) {
      this.#showStep('phone');
    } else {
      this.#finish();
    }
  }

  /** @param {SubmitEvent} event */
  async submitPhone(event) {
    event.preventDefault();
    if (this.#busy || !this.refs.phoneInput || !this.refs.countryCode) return;

    const phone = toE164(this.refs.phoneInput.value, this.refs.countryCode.value);
    if (!phone) {
      this.#error('phone', this.#t('phone_invalid'));
      return;
    }
    this.#error('phone', '');

    this.#setBusy('phone', true);
    const ok = await this.#subscribe('sms', { email: this.#email, phone });
    this.#setBusy('phone', false);
    if (!this.isConnected) return;
    if (!ok) {
      this.#error('phone', this.#t('error_generic'));
      return;
    }

    this.#phone = phone;
    if (this.#recordsHistory) rememberContact({ phone });
    // With double opt-in Klaviyo only subscribes the number once the visitor
    // replies to its confirmation text, so the reward screen says so.
    this.#sms = this.dataset.smsDoubleOptIn === 'true' ? 'pending' : 'requested';
    this.#identify();
    this.#announce('sms');
    this.#finish();
  }

  get #preview() {
    if (this.dataset.designMode === 'true') return true;
    return (
      ['localhost', '127.0.0.1', '[::1]'].includes(location.hostname) &&
      new URLSearchParams(location.search).get('signup_preview') === '1'
    );
  }

  /** An unpublished or development theme, viewed through a preview link. */
  get #previewTheme() {
    const role = this.dataset.themeRole;
    return Boolean(role) && role !== 'main';
  }

  /** Only the live theme, outside test mode, remembers closes and signups. */
  get #recordsHistory() {
    return !this.#preview && !this.#previewTheme;
  }

  get #offerLabel() {
    const percent = this.querySelector('[data-offer-percent]')?.textContent?.replace('%', '') ?? '';
    return this.#t('offer', { percent });
  }

  /**
   * @param {number} delay
   * @param {boolean} [automatic] - A timed open, which honours the Klaviyo "known profile" check.
   */
  #schedule(delay, automatic = false) {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(async () => {
      if (!this.isConnected) return;
      if (document.querySelector('dialog[open]')) {
        this.#schedule(5000, automatic);
        return;
      }
      if (automatic) {
        const plan = await this.#plan();
        if (plan === 'skip' || !this.isConnected || document.querySelector('dialog[open]')) return;
        if (plan === 'subscribed') this.#markKnown();
      }
      this.open();
    }, delay);
  }

  #finish() {
    this.#finished = true;
    if (this.#recordsHistory) this.#save('completed', { sms: this.#sms });
    this.#track(SignupPopupComponent.completedMetric);
    this.#showStep('success');
  }

  /** @param {Step} step */
  #showStep(step) {
    this.#step = step;
    this.dataset.currentStep = step;
    for (const panel of this.querySelectorAll('[data-step]')) {
      if (panel instanceof HTMLElement) panel.hidden = panel.dataset.step !== step;
    }

    const current = this.querySelector(`[data-step="${step}"]`);
    if (!(current instanceof HTMLElement)) return;

    const heading = current.querySelector('h2');
    if (heading) this.refs.dialog.setAttribute('aria-labelledby', heading.id);
    // showModal() focuses the autofocus element: the visible step's heading,
    // so the dialog is announced by its title without a focus ring on the close button.
    for (const title of this.querySelectorAll('[data-step] h2')) title.toggleAttribute('autofocus', title === heading);

    if (step === 'success') {
      for (const notice of this.querySelectorAll('[data-sms-notice]')) {
        if (notice instanceof HTMLElement) notice.hidden = notice.dataset.smsNotice !== this.#sms;
      }
    }

    if (!this.refs.dialog.open) return;
    heading?.focus({ preventScroll: true });
    const sheet = this.querySelector('.signup-flow__sheet');
    if (sheet) sheet.scrollTop = 0;
  }

  /**
   * Subscribes the profile to the channel's Klaviyo list. Resolves false on
   * any failure so the form can show an error and keep the typed value.
   *
   * @param {'email' | 'sms'} channel
   * @param {{ email?: string, phone?: string }} identity
   * @returns {Promise<boolean>}
   */
  async #subscribe(channel, { email, phone }) {
    if (this.#preview) return true;

    const company = this.dataset.klaviyoKey;
    const list = channel === 'email' ? this.dataset.klaviyoEmailList : this.dataset.klaviyoSmsList;
    if (!company || !list) return false;

    /** @type {Record<string, unknown>} */
    const attributes = {
      properties: this.#profileProperties(),
      subscriptions: { [channel]: { marketing: { consent: 'SUBSCRIBED' } } },
    };
    if (email) attributes.email = email;
    if (phone) attributes.phone_number = phone;

    try {
      const response = await fetch(`${SignupPopupComponent.subscriptionsEndpoint}?company_id=${encodeURIComponent(company)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', revision: SignupPopupComponent.klaviyoRevision },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: SignupPopupComponent.customSource,
              profile: { data: { type: 'profile', attributes } },
            },
            relationships: { list: { data: { type: 'list', id: list } } },
          },
        }),
        signal: this.#signal(12000),
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Records a metric against the profile through Klaviyo's client Events API.
   * Fire-and-forget: a blocked or failed request changes nothing for the visitor.
   *
   * @param {string} metric
   */
  #track(metric) {
    const company = this.dataset.klaviyoKey;
    if (this.#preview || !company || !this.#email) return;

    /** @type {Record<string, unknown>} */
    const profile = { email: this.#email };
    if (this.#phone) profile.phone_number = this.#phone;

    fetch(`${SignupPopupComponent.eventsEndpoint}?company_id=${encodeURIComponent(company)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: SignupPopupComponent.klaviyoRevision },
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            metric: { data: { type: 'metric', attributes: { name: metric } } },
            properties: {
              ...this.#profileProperties(),
              signup_popup_sms: this.#sms || 'skipped',
            },
            profile: { data: { type: 'profile', attributes: profile } },
          },
        },
      }),
      keepalive: true,
      signal: this.#signal(8000),
    }).catch(() => {});
  }

  /**
   * Ties Klaviyo's onsite script to the profile so later browsing, forms
   * and `isIdentified` checks all recognise this visitor.
   */
  #identify() {
    const onsite = getOnsiteKlaviyo();
    if (this.#preview || !onsite || !this.#email) return;

    /** @type {Record<string, unknown>} */
    const identity = { email: this.#email, ...this.#profileProperties() };
    if (this.#phone) identity.phone_number = this.#phone;

    try {
      onsite.push(['identify', identity]);
    } catch {
      // Tracking must never block the flow.
    }
  }

  /**
   * Asks the onsite script whether Klaviyo already knows this visitor
   * (an earlier identify, or a click-through from a Klaviyo email or text).
   * Resolves false when the script is missing, blocked, or slow.
   *
   * @returns {Promise<boolean>}
   */
  #isKnownProfile() {
    const onsite = getOnsiteKlaviyo();
    if (!onsite) return Promise.resolve(false);

    return new Promise((resolve) => {
      const timer = setTimeout(() => resolve(false), 1500);
      try {
        onsite.push([
          'isIdentified',
          (/** @type {unknown} */ identified) => {
            clearTimeout(timer);
            resolve(identified === true);
          },
        ]);
      } catch {
        clearTimeout(timer);
        resolve(false);
      }
    });
  }

  #profileProperties() {
    /** @type {Record<string, string>} */
    const properties = {
      signup_popup_page: location.pathname,
      signup_popup_offer: this.#offerLabel,
    };
    if (this.#answer) {
      properties.signup_popup_quiz_answer_key = this.#answer;
      properties.signup_popup_quiz_answer = this.#answerLabel;
    }
    if (this.#answerSource) properties.signup_popup_quiz_answer_source = this.#answerSource;
    if (this.#quizReason) properties.signup_popup_quiz_reason = this.#quizReason;
    return properties;
  }

  /**
   * @param {'email' | 'sms'} channel
   * @param {'popup' | 'klaviyo-form'} [source]
   */
  #announce(channel, source = 'popup') {
    const detail = { channel, source };
    // Only successful real popup submissions supply contact to analytics.
    // Klaviyo-hosted forms have their own event and must not reuse popup state.
    if (!this.#preview && source === 'popup') {
      detail.email = this.#email;
      detail.phone = this.#phone;
    }
    document.dispatchEvent(new CustomEvent('signup-popup:subscribed', { detail }));
  }

  /**
   * @param {'email' | 'phone'} channel
   * @param {boolean} busy
   */
  #setBusy(channel, busy) {
    this.#busy = busy;
    const button = channel === 'email' ? this.refs.emailSubmit : this.refs.phoneSubmit;
    if (!button) return;

    button.disabled = busy;
    button.setAttribute('aria-busy', String(busy));
    for (const span of button.querySelectorAll('[data-label], [data-busy]')) {
      if (span instanceof HTMLElement) span.hidden = span.hasAttribute('data-busy') ? !busy : busy;
    }
  }

  /**
   * @param {'email' | 'phone'} channel
   * @param {string} message - Empty to clear.
   */
  #error(channel, message) {
    const input = channel === 'email' ? this.refs.emailInput : this.refs.phoneInput;
    const error = channel === 'email' ? this.refs.emailError : this.refs.phoneError;
    if (!input || !error) return;

    error.textContent = message;
    error.hidden = !message;
    input.setAttribute('aria-invalid', String(Boolean(message)));
    if (message) input.focus();
  }

  /**
   * Reads the live first-order bundle tiers from the offer product's buy box
   * (on this page, or fetched once) and writes the best one into the copy.
   * Falls back to the section's configured tiers.
   */
  async #syncOffer() {
    let tiers = parseTiers(this.dataset.tiers || '');
    const source = new URL(this.dataset.offerUrl || '/', location.origin);
    if (source.origin !== location.origin) return;

    /** @param {ParentNode} doc */
    const readTiers = (doc) =>
      [...doc.querySelectorAll('pdp-pack-size-component input[data-percent]')]
        .filter((input) => input instanceof HTMLInputElement)
        .map((input) => ({ count: Number(input.value), percent: Number(input.dataset.percent) }))
        .filter(validTier);

    const buyBox = document.querySelector(`pdp-buy-box-component[data-product-id="${this.dataset.offerProductId}"]`);
    const local = buyBox ? readTiers(buyBox.closest('.shopify-section') || document) : [];
    if (local.length) {
      tiers = local;
    } else {
      try {
        const response = await fetch(source, { signal: this.#signal(4000) });
        if (response.ok) {
          const fresh = readTiers(new DOMParser().parseFromString(await response.text(), 'text/html'));
          if (fresh.length) tiers = fresh;
        }
      } catch {
        // Keep the configured fallback tiers.
      }
    }

    if (!this.isConnected || !tiers.length) return;
    tiers = [...new Map(tiers.map((tier) => [tier.count, tier])).values()].sort((a, b) => a.count - b.count);
    const best = tiers.reduce((a, b) => (b.percent > a.percent ? b : a));
    const percent = String(best.percent);

    for (const label of this.querySelectorAll('[data-offer-percent]')) label.textContent = `${percent}%`;
    for (const element of this.querySelectorAll('[data-offer-template]')) {
      if (element instanceof HTMLElement) element.textContent = (element.dataset.offerTemplate ?? '').replaceAll('[percent]', percent);
    }
    for (const terms of this.querySelectorAll('[data-offer-terms]')) {
      terms.textContent = this.#t('offer_terms', { percent, count: best.count });
    }

    if (this.dataset.customShopLink === 'true') return;
    source.searchParams.set('tier', String(best.count));
    for (const link of this.querySelectorAll('[data-shop-offer]')) {
      if (link instanceof HTMLAnchorElement) link.href = source.pathname + source.search;
    }
  }

  /**
   * A fetch signal that aborts after `timeout` ms or when the component leaves the page.
   *
   * @param {number} timeout
   */
  #signal(timeout) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    this.#controller?.signal.addEventListener('abort', () => {
      clearTimeout(timer);
      controller.abort();
    }, { once: true });
    return controller.signal;
  }

  /**
   * @param {string} key
   * @param {Record<string, string | number>} [values]
   */
  #t(key, values = {}) {
    let value = this.#strings[key] || key;
    for (const [name, text] of Object.entries(values)) value = value.replaceAll(`[${name}]`, String(text));
    return value;
  }

  /**
   * What the timed open should do: show the offer, show the "already
   * subscribed" screen, or stay quiet.
   *
   * - A subscriber is never interrupted: the timed open stays quiet until the
   *   "show again" period ends and the offer returns from the top (with 0 the
   *   offer never returns). The "already subscribed" screen is for subscribers
   *   who open the popup themselves.
   * - A visitor Klaviyo recognizes (say, arriving from an email or text)
   *   counts as a subscriber, remembered from the first sighting.
   * - A close hides the popup for the hide period.
   * - Preview themes skip the cooldowns so preview links always show something.
   *
   * @returns {Promise<'offer' | 'subscribed' | 'skip'>}
   */
  async #plan() {
    let saved = this.#load();
    if (this.#adoptExternalSignup()) saved = this.#load();
    const hide = Number(this.dataset.hideDays || 7) * DAY;
    const showAgain = Number(this.dataset.showAgainDays || 0) * DAY;
    const since = (/** @type {number | undefined} */ time) => Date.now() - (time ?? 0);
    const last = Math.max(saved.at ?? 0, saved.closed ?? 0);

    if (isSubscribed(saved)) {
      if (this.#previewTheme) return 'subscribed';
      if (showAgain > 0 && since(saved.at) >= showAgain) {
        this.#clear();
        saved = {};
      } else {
        return 'skip';
      }
    } else if (!this.#previewTheme && saved.status === 'dismissed' && since(last) < hide) {
      return 'skip';
    }

    if (this.dataset.skipKnownProfiles === 'true' && !this.#ignoreIdentity && (await this.#isKnownProfile())) {
      if (this.#previewTheme) return 'subscribed';
      this.#markKnown();
      return 'skip';
    }
    return 'offer';
  }

  /**
   * Whether this browser counts as subscribed: by the popup's own record,
   * or by a signup another surface (the quiz) noted under the shared key,
   * which becomes the popup's own record on first sight. Checked before a
   * timed open and on manual opens alike.
   *
   * @returns {boolean}
   */
  #adoptExternalSignup() {
    if (isSubscribed(this.#load())) return true;
    const external = readSignup();
    if (!external?.at || !this.#recordsHistory) return false;
    this.#save('subscribed', { source: external.source ?? 'external' });
    return true;
  }

  /** The visitor is a subscriber: remember it, so a manual open says so. */
  #markKnown() {
    this.#known = true;
    if (this.#recordsHistory && !isSubscribed(this.#load())) this.#save('subscribed', { source: 'klaviyo' });
  }

  /**
   * Merges fields into the stored record without changing its status or time.
   *
   * @param {Partial<SignupRecord>} extra
   */
  #touch(extra) {
    try {
      localStorage.setItem(this.dataset.storageKey ?? '', JSON.stringify({ ...this.#load(), ...extra }));
    } catch {
      // Private mode or blocked storage.
    }
  }

  #clear() {
    try {
      localStorage.removeItem(this.dataset.storageKey ?? '');
    } catch {
      // Nothing stored, or storage is blocked.
    }
  }

  /** @returns {SignupRecord} */
  #load() {
    try {
      return JSON.parse(localStorage.getItem(this.dataset.storageKey ?? '') || '{}') || {};
    } catch {
      return {};
    }
  }

  /**
   * @param {'dismissed' | 'subscribed' | 'completed'} status
   * @param {Partial<SignupRecord>} [extra]
   */
  #save(status, extra = {}) {
    try {
      localStorage.setItem(this.dataset.storageKey ?? '', JSON.stringify({ ...this.#load(), ...extra, status, at: Date.now() }));
    } catch {
      // Private mode or blocked storage: the popup simply asks again next visit.
    }
  }

  /**
   * iOS Safari colors its status bar from the page's `theme-color` (or samples
   * the page when it is empty, which picks up the dimmed backdrop). While the
   * dialog is open, match the strip to the dialog so the popup reads as
   * full screen; `#handleClose` puts the previous value back.
   */
  #tintBrowserChrome() {
    const meta = document.head.querySelector('meta[name="theme-color"]');
    if (!meta) return;
    this.#previousThemeColor = meta.getAttribute('content') ?? '';
    changeMetaThemeColor(getComputedStyle(this.refs.dialog).getPropertyValue('--signup-flow-bg').trim());
  }

  /** @type {string | null} */
  #previousThemeColor = null;

  #handleClose = () => {
    clearTimeout(this.#advanceTimer);
    clearTimeout(this.#celebrateTimer);
    if (this.#previousThemeColor !== null) {
      document.head.querySelector('meta[name="theme-color"]')?.setAttribute('content', this.#previousThemeColor);
      this.#previousThemeColor = null;
    }
    if (!this.#recordsHistory || this.#finished) return;
    if (isSubscribed(this.#load())) this.#touch({ closed: Date.now() });
    else this.#save('dismissed', { closed: Date.now() });
  };

  /** @param {Event} event */
  #handleTrigger = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('[data-signup-popup-open], a[href="#signup-popup"]')) return;
    event.preventDefault();
    this.open();
  };

  /**
   * A Klaviyo-hosted form on the same page (footer embed, another popup)
   * subscribed this visitor: remember it so the popup stops asking.
   *
   * @param {Event} event
   */
  #handleKlaviyoForm = (event) => {
    const detail = event instanceof CustomEvent ? event.detail : undefined;
    if (!detail || !['submit', 'stepSubmit'].includes(detail.type)) return;

    const meta = detail.metaData ?? {};
    const channel = isFilled(meta.$phone_number) ? 'sms' : isFilled(meta.$email) ? 'email' : '';
    if (!channel || !this.#recordsHistory) return;

    clearTimeout(this.#timer);
    rememberContact({
      email: isFilled(meta.$email) && EMAIL_PATTERN.test(meta.$email.trim()) ? meta.$email.trim() : '',
      phone: isFilled(meta.$phone_number) ? toE164(meta.$phone_number) : '',
    });
    this.#save('completed', channel === 'sms' ? { sms: 'requested', source: 'klaviyo-form' } : { source: 'klaviyo-form' });
    this.#announce(channel, 'klaviyo-form');
  };

  /** @param {Event} event */
  #handleSectionSelect = (event) => {
    if (!(event instanceof CustomEvent) || event.detail?.sectionId !== this.dataset.sectionId) return;
    const step = this.dataset.previewStep;
    if (isStep(step)) this.#step = step;
    this.open();
  };

  /** @param {Event} event */
  #handleSectionDeselect = (event) => {
    if (event instanceof CustomEvent && event.detail?.sectionId === this.dataset.sectionId) this.closeDialog();
  };
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const DAY = 86400000;
/** How long a finished product quiz keeps answering the popup's question. */
const QUIZ_ANSWER_MAX_AGE = 30 * DAY;

/**
 * The product quiz's reason keys (snippets/quiz-data.liquid `reasons`) that
 * read as one of the popup's four goals. The rest skip the question without
 * picking a goal.
 *
 * @type {Record<string, string>}
 */
const QUIZ_REASON_GOALS = {
  hydration: 'hydration',
  portable: 'hydration',
  travel: 'hydration',
  gut: 'gut_health',
  immune: 'gut_health',
  energy: 'energy',
  performance: 'energy',
  recovery: 'energy',
  value: 'routine',
  first_timer: 'routine',
};

/**
 * @param {unknown} value
 * @returns {value is Step}
 */
function isStep(value) {
  return typeof value === 'string' && SignupPopupComponent.steps.includes(/** @type {Step} */ (value));
}

/** @param {SignupRecord} record */
function isSubscribed(record) {
  return record.status === 'subscribed' || record.status === 'completed';
}

/** @param {unknown} value */
function isFilled(value) {
  return typeof value === 'string' && value.trim() !== '';
}

/**
 * The onsite Klaviyo object installed by the app embed. Before klaviyo.js
 * loads it is a queue whose `push` replays once the script is ready.
 *
 * @returns {{ push: (call: unknown[]) => unknown } | null}
 */
function getOnsiteKlaviyo() {
  const onsite = /** @type {{ klaviyo?: { push?: unknown } }} */ (window).klaviyo;
  if (!onsite || typeof onsite.push !== 'function') return null;
  return /** @type {{ push: (call: unknown[]) => unknown }} */ (onsite);
}

/** @param {OfferTier} tier */
function validTier(tier) {
  return (
    Number.isInteger(tier.count) &&
    tier.count > 0 &&
    tier.count <= 20 &&
    Number.isFinite(tier.percent) &&
    tier.percent >= 0 &&
    tier.percent < 100
  );
}

/** @param {string} value - "count:percent" pairs, comma separated. */
function parseTiers(value) {
  return value
    .split(',')
    .map((pair) => {
      const [count, percent] = pair.trim().split(':').map(Number);
      return { count, percent };
    })
    .filter(validTier);
}

if (!customElements.get('signup-popup-component')) customElements.define('signup-popup-component', SignupPopupComponent);
